/**
 * LiveSidebarPreview - Right panel of the sidebar builder
 *
 * Shows a real-time miniature sidebar preview powered by the resolver.
 * Updates as the user drags items around in the canvas.
 */

import React from 'react';
import { RefreshCw, AlertTriangle, CheckCircle, Box } from 'lucide-react';
import { MODULE_ICONS } from '../../../constants/profile_constants.js';

function MiniSidebarGroup({ group, allFeatures }) {
  const items = group.items || [];
  return (
    <div className="mb-2">
      <div className="text-[10px] text-slate-500 uppercase tracking-wider px-2 py-0.5">
        {group.label || group.code}
      </div>
      {items.map((item, idx) => {
        const id = typeof item === 'string' ? item : (item.id || item.view_code);
        const label = typeof item === 'string'
          ? (allFeatures.find(f => f.id === id)?.label || id)
          : (item.label || item.view_name || id);
        const Icon = MODULE_ICONS[id] || Box;
        return (
          <div
            key={`${id}-${idx}`}
            className="flex items-center gap-2 px-2 py-1 text-xs text-slate-400 rounded hover:bg-slate-800/50"
          >
            <Icon size={11} className="flex-shrink-0" />
            <span className="truncate">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

export function LiveSidebarPreview({
  resolvedGroups,
  warnings,
  isResolving,
  error,
  allFeatures,
  builderState,
}) {
  // If no resolver output, build preview from builder state directly
  const previewGroups = resolvedGroups
    ? (Array.isArray(resolvedGroups)
        ? resolvedGroups
        : Object.entries(resolvedGroups).map(([code, g]) => ({ code, ...g })))
    : builderState?.categories?.map(cat => ({
        code: cat.code,
        label: cat.label,
        items: cat.items,
      })) || [];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300">
            Aperçu en direct
          </h3>
          {isResolving && (
            <RefreshCw size={14} className="text-blue-400 animate-spin" />
          )}
        </div>
        <div className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
          {resolvedGroups ? 'Résolution live' : 'Aperçu local'}
        </div>
      </div>

      {/* Mini sidebar */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="bg-slate-900 rounded-lg border border-slate-700 overflow-hidden">
          {/* App header */}
          <div className="p-2 border-b border-slate-700 flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-blue-500/20 flex items-center justify-center">
              <span className="text-[8px] text-blue-400 font-bold">FM</span>
            </div>
            <div>
              <div className="text-[10px] font-bold text-white">FleetManager</div>
            </div>
          </div>

          {/* Groups */}
          <div className="p-1.5 max-h-[360px] overflow-y-auto">
            {previewGroups.length === 0 ? (
              <div className="text-center py-6 text-xs text-slate-500">
                Aucun groupe
              </div>
            ) : (
              previewGroups.map((group, idx) => (
                <MiniSidebarGroup
                  key={group.code || idx}
                  group={group}
                  allFeatures={allFeatures}
                />
              ))
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400 flex items-start gap-2">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <div className="text-[10px] font-medium text-amber-400 mb-1">Avertissements</div>
            {warnings.map((w, i) => (
              <div key={i} className="text-[10px] text-amber-300">- {w}</div>
            ))}
          </div>
        )}

        {/* Stats */}
        {!isResolving && previewGroups.length > 0 && (
          <div className="mt-2 flex items-center justify-center gap-1 text-[10px] text-gray-400 dark:text-slate-500">
            <CheckCircle size={10} className="text-green-500" />
            <span>
              {previewGroups.length} groupes,{' '}
              {previewGroups.reduce((s, g) => s + (g.items?.length || 0), 0)} items
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default LiveSidebarPreview;
